<template>
  <div class="container h-100 d-flex bg-white px-4 mb-4">
    <div class="row">
      <div class="col">
        <h1>
          OOYYO AI
        </h1>
        <article>
          <h2>
            1. Uvod
          </h2>
          <article>
            <h3>
              1.1 Uopšteno o AI
            </h3>
            <article>
              <p>
                Veštačka inteligencija je oblast informatike i računarstva koja
                se bavi simuliranjem inteligentnog ponašanja mašina sa namerom
                da u rešavanju problema budu bar jednako uspešni, ako ne i
                uspešniji od ljudi. U slučajevima gde je problem egzaktno
                definisan i podleže striktno matematičkim pravilima rešavanje je
                izvesnije, ali izazov se sastoji u slučajevima kada je uključena
                i emotivna komponenta ljudi koju je potrebno kompenzovati kako
                bi se ponašanje mašine što više približilo ciljanom.
              </p>
              <p>
                Komponente veštačke inteligencije su učenje, razmišljanje i
                donošenje odluke.
              </p>
              <p>
                Učenje je proces memorisanja baze znanja i pravila, bilo da se
                podaci unose eksterno, bilo da se mašini dozvoli da do toga dođe
                autonomno preko pokušaja i grešaka, ukoliko je to moguće. Na ove
                načine mašina stiče iskustvo koje je neophodno za dalje korake.
              </p>
              <p>
                Razmišljanje je proces zaključivanja gde se u trenutnoj
                situaciji prepoznaje analogija sa ranije stečenim znanjem. Može
                biti deduktivno i induktivno. Deduktivno zaključivanje se
                zasniva na opštem pravilu primenjenom na pojedinačni slučaj i
                ono je uvek validno, ako je pravilo ispravno i pretpostavka
                tačna. Induktivno zaključivanje je usvajanje opšteg pravila iz
                niza pojedinačnih slučajeva, ako se utvrdi da ne postoji slučaj
                gde pravilo ne važi.
              </p>
              <p>
                Donošenje odluke je rezultujući korak gde mašina daje odgovor na
                postavljeno pitanje, odnosno odlučuje se za neku akciju u
                situaciji u kojoj se našla, na osnovu ranije naučenog i
                primenjenog razmišljanja.
              </p>
            </article>

            <h3>
              1.2. Istorijat
            </h3>
            <article>
              <p>
                Prvi trag nekog elementa veštačke inteligencije se može
                pripisati Aristotelu čiji su silogizmi bili preteča formalne
                iskazne logike.
              </p>

              <p>
                U modernoj istoriji temelji razvoja veštačke inteligencije su
                postavljeni sredinom 20. veka. Jedan od bitnijih događaja je bio
                objavljivanje članka “Računari i inteligencija” 1950. godine od
                strane Alana Tjuringa. On je dao predlog kriterijuma za procenu
                kvaliteta veštačke inteligencije koji se naziva “Tjuringov
                test”. U testu učestvuju jedan računar i jedan čovek koji
                odgovaraju uporedo na pitanja i razgovaraju međusobno. Drugi
                čovek je u funkciji sudije koji čita odgovore, prati
                konverzaciju i tako pokušava da proceni koji učesnik je računar,
                a koji čovek. Ukoliko sudija ne može da bude potpuno siguran ko
                je ko, mašina je prošla test.
              </p>

              <p>
                Prvi veliki skup naučnika i istraživača na temu veštačke
                inteligencije se odigrao u Dartmudu 1956. godine kada je ova
                disciplina i formalno dobila ime, gde su bili prisutni
                predstavnici univerziteta Prinston i MIT, kao i IBM-a. Na tom
                skupu su donete odluke koje su postavile osnove za dalji razvoj
                ove oblasti.
              </p>
            </article>

            <h3>
              1.3. Neuronske mreže – principi i matematička pozadina
            </h3>
            <article>
              <p>
                Sistemi veštačke inteligencije se mogu podeliti na ekspertske
                sisteme i neuronske mreže. Za potrebe rešavanja problema koji su
                predmet ovog projekta korišćene su neuronske mreže, pa će
                poseban akcenat u daljem tekstu biti na njima.
              </p>

              <p>
                Veštačka neuronska mreža je simulacija strukture i
                funkcionisanja ljudskog mozga gde je osnovna jedinica neuron
                ciji su ulazi i izlazi povezani sa susednim neuronima i na taj
                način formiraju mrežu.
              </p>

              <p>
                Veštački neuron koji prima ulazne informacije i predaje izlaz se
                naziva perceptron.
              </p>

              <p class="text-center">
                <img class="mw-100" src="@/images/ai/perceptron.png" />
              </p>

              <p>
                U opštem slučaju perceptroni su međusobno povezani u mreže pri
                čemu mreža može imati proizvoljnu topologiju i proizvoljan broj
                unutrašnjih (skrivenih) nivoa, ali obavezno mora imati ulazni i
                izlazni nivo.
              </p>

              <p class="text-center">
                <img class="mw-100" src="@/images/ai/neural_network.png" />
              </p>

              <p>
                Matematički gledano, neuronska mreža predstavlja funkcionalnu
                zavisnost f : X → O gde je X ulazni vektor, a O (output)
                izlazni.
              </p>

              <p>
                Osnovna jedinica strukture podataka u neuronskim mrežama je
                tenzor - višedimenzioni niz. Tenzor je definisan brojem osa
                (rangom), oblikom, odnosno dimenzijom po svakoj osi i tipom
                podatka.
              </p>

              <p class="text-center">
                <img class="mw-100" src="@/images/ai/tensor_rank.png" />
              </p>

              <p>Neki realni primeri:</p>

              <ul>
                <li>
                  Ako bismo imali spisak od 100 000 ljudi koji sadrži JMBG,
                  poštanski broj i mesečnu zaradu kao podatke to bismo
                  predstavili tenzorom ranga 2 (matricom) dimenzija (100000, 3)
                </li>
                <li>
                  Ako imamo 500 slika u boji veličine 256 * 256 piksela i svaki
                  piksel je definisan sa 3 kanala (RGB) onda bismo te podatke
                  predstavili tenzorom ranga 4, dimenzija (500, 256, 256, 3)
                </li>
              </ul>

              <p>
                Ovako predstavljeni podaci služe da se neuronska mreža nauči,
                odnosno istrenira, gde se definiše koji su to parametri ulazni,
                a koji izlazni. Treniranje mreže je složen proces podešavanja
                svih mogućih parametara u cilju smanjenja greške. Kada se mreža
                istrenira može se koristiti za rešavanje problema za koji je
                projektovana.
              </p>
            </article>
          </article>

          <h2>
            2. Potencijalne primene na OOYYO sistem
          </h2>
          <article>
            <p>
              Za neuronske mreže bitna stavka je količina dostupnih podataka
              koja može da se upotrebi za treniranje. OOYYO ima na raspolaganju
              baze podataka koje pored toga sto sadrže veliki broj rekorda imaju
              odliku da su dinamičke u vremenu, kako sa strane ponude, pošto
              oglasi imaju ograničen životni vek i stalno se menjaju, tako i sa
              strane potražnje, jer korisnici pretražuju sajt kontinualno.
            </p>
            <h3>
              2.1. Procena vrednosti vozila
            </h3>
            <article>
              <p>
                Kao prvi zadatak odlučeno je da se krene sa realizacijom
                neuronske mreže koja treba da što tačnije odgovara na pitanje:
                “Kolika je realna cena oglašenog vozila sa konkretnim
                informacijama koje su dostupne iz oglasa?” Ovo pitanje se samo
                po sebi nameće i nije prvi put da se pokušava dati odgovor na
                njega.
              </p>
              <h4>
                2.1.1. Postojeće rešenje - linearna regresija
              </h4>
              <p>
                OOYYO sistem već ima realizovanu funkcionalnost koja daje
                procenu na sledeći način:
              </p>
              <p>
                Ako se uzmu u obzir zemlja pretrage, marka, model i godište kao
                fiksni parametri, svi oglasi koji sadrže iste takve parametre
                mogu da se svrstaju u zasebne grupe. Na taj način možemo u bilo
                kojoj grupi da posmatramo zavisnost cene od pređene kilometraže.
                Svaki oglas se može predstaviti kao jedna tačka u ravni gde je
                na x osi kilometraža, a na y osi cena, pa se cela grupa
                predstavlja kao skup tačaka u toj ravni.
              </p>
              <p class="text-center">
                <img class="mw-100" src="@/images/ai/linear_regression.png" />
              </p>
              <p>
                Narandžastom tačkom je obeležen konkretan oglas, a plavim
                tačkama ostali primerci iz iste grupe (VW Golf 2017. Nemačka)
                Prava označava procenjenu zavisnost vrednosti vozila od
                kilometraže koja je obračunata upotrebom statističkog modela
                linearne regresije kad se primeni nad celokupnom populacijom te
                grupe. Linearna regresija ima za cilj da određivanjem optimalnih
                parametara prave pronađe takvo rešenje da je srednje kvadratno
                odstupanje populacije od prave minimalno. Kada se utvrde
                parametri prave, ukoliko je tačka iznad nje, oglašena vrednost
                tog vozila se smatra precenjenom, ako je ispod prave, onda je
                potcenjena.
              </p>
              <p>
                Nažalost, ovom metodom nije uvek moguće dobiti željeni rezultat.
                Problem nastaje u slučajevima kada je grupa suviše mala, pa
                procena nad malim uzorkom nije merodavna ili u slučajevima gde
                neka grupa pokazuje anomaliju (na primer, rast cene sa porastom
                kilometraže) Druga mana ove metode je što su svi ostali
                parametri koji opisuju vozilo zanemareni, a mogu značajno da
                utiču na realnu vrednost. Treća mana je što se primenom linearne
                regresije dobija najjednostavnija matematička aproksimacija
                funkcionalne zavisnosti – linearna funkcija, što samo po sebi
                unosi grešku jer u opštem slučaju zavisnost može biti znatno
                složenija, krivolinijska. Zbog svih navedenih razloga pristupilo
                se primeni neuronskih mreža, kao sofisticiranoj metodologiji za
                bolje rešenje ovog pitanja.
              </p>
              <h4>
                2.1.2. Novo rešenje – neuronske mreže
              </h4>
              <p>
                Neuronske mreže imaju takvu osobinu da mogu da uzmu u obzir sve
                ili neki podskup relevantnih parametara koji utiču na cenu. U
                ovom slučaju bazis parametara je: model (implicitno i make),
                trim, tip karoserije, tip goriva, tip menjača, boja, grad,
                godište, kilometraža, zapremina i snaga.
              </p>
              <p>
                Spisak parametara se po svojoj prirodi može podeliti u 2 grupe:
                kategorijski i numerički. Kategorijski parametri su oni čija
                vrednost daje opis neke karakteristike vozila koja izvorno nije
                predstavljena numeričkom vrednošću. Od navedenih tu spadaju
                model, trim, tip karoserije, tip goriva, tip menjača, boja i
                grad. Numerički parametri su godište, kilometraža, zapremina i
                snaga.
              </p>
              <p>
                Kao što je rečeno, neuronska mreža kao ulaz mora da dobije
                tenzor, što znači da je potrebno kategorijske parametre na neki
                način transformisati u numeričku reprezentaciju što je
                postignuto metodom “one-hot encoding” Na ovaj način su
                postignuti početni koraci mašinskog učenja - prikupljanje i
                priprema podataka. Nakon njih predstojeći koraci su izbor
                modela, treniranje modela, evaluacija modela, podešavanje
                parametara i predikcija.
              </p>
              <p>
                Predikcija predstavlja konačni rezultat razvoja neuronske mreže,
                odnosno njenu eksploataciju i vrši se tako što za proizvoljni
                oglas se uzmu svi ulazni parametri, proslede se mreži koja kao
                izlaz daje odgovor na pitanje “Koliko ovakav automobil vredi?”
              </p>
              <p>
                Unapređena funkcionalnost bi mogla da bude kada bi se korisniku
                dalo na raspolaganje da konfiguriše parametre željenog vozila,
                iako takav oglas koji bi odgovarao opisu možda trenutno ne
                postoji, kako bi dobio procenu koliko vredi takvo vozilo,
                hipotetički.
              </p>
            </article>
            <h3>
              2.2. Detekcija anomalija podataka na krolu
            </h3>
            <article>
              <p>
                Potencijalna upotreba neuronskih mreža može biti detekcija
                anomalija. U opštem slučaju detekcija anomalija se vrši tako što
                se posmatraju svi članovi serije i odstranjuju se oni čiji
                parametri dovoljno odstupaju od nekog kriterijuma tako da se
                proglase nevalidnim.
              </p>
              <p>
                U slučaju OOYYO-a, kroz krol sistem prolaze skupovi oglasa na
                različite načine. Uzroci zašto su neki podaci nevalidni mogu
                biti u pogrešnom izvornom sadržaju oglasa ili u grešci prilikom
                obrade (parsiranja) U zavisnosti od ozbiljnosti anomalije i
                ucestalosti te anomalije u oglasima na odredjenom sajtu moguće
                bi bilo isključiti pojedinačni oglas ili dati obaveštenje da je
                u pitanju sistemska greška koja utiče na veći broj oglasa i da
                je potrebno uraditi detaljnu dijagnostiku da bi se pronašao
                tačan uzrok.
              </p>
              <p>
                Razvojem ovakve neuronske mreže bi se dobilo na kvalitetu
                podataka na sajtu, tako što bi se škart ili potpuno uklonio ili
                značajno smanjio, kao i što bi se ubrzala detekcija i
                servisiranje većih problema na strani krola.
              </p>
            </article>
            <h3>
              2.3. Uporedna analiza ponude i potražnje
            </h3>
            <article>
              <p>
                Osim velikog skupa podataka koji čine oglasi na strani ponude,
                OOYYO prikuplja brojne informacije o ponašanju svojih korisnika
                dok posećuju sajt: geolokacija, tip uredjaja, parametri
                pretrage, način kretanja kroz sajt… što čini dobar osnov za
                utvrđivanje parametara na strani potražnje.
              </p>
              <p>
                Izradom neuronske mreže koja bi pratila uporedo ponašanje
                korisnika u skladu sa ponudom bi mogla da odgovori na neka
                interesantna pitanja. Odgovori na ta pitanja bi bili značajni i
                za prodavce vozila i za kupce, možda i za proizvođače automobila
                i rezervnih delova, kao podrška raznim granama marketinga...
              </p>
              <p>
                Neka od tih pitanja bi mogla biti:
              </p>
              <ul>
                <li>
                  Koja je zavisnost između ponude i potražnje vozila po
                  određenim kriterijumima?
                </li>
                <li>
                  Kakav bi sponzorisani sadržaj mogao biti serviran korisniku
                  koji po utvrđenim kriterijumima pripada nekoj populaciji?
                </li>
                <li>
                  Komparacija popularnosti 2 ili vise grupe vozila po različitim
                  parametrima?
                </li>
                <li>
                  Kako se rangira neki skup vozila u odnosu na konkurenciju
                  tokom eksploatacionog perioda?
                </li>
              </ul>
            </article>
          </article>

          <h2>
            3. Mašinsko učenje u zadatku procene vrednosti vozila
          </h2>
          <article>
            <p>
              U opštem slučaju algoritam koraka i postupaka u mašinskom učenju
              se može predstaviti kao na sledećem dijagramu:
            </p>
            <p class="text-center">
              <img class="mw-100" src="@/images/ai/ml_steps.jpg" />
            </p>
            <p>
              Između koraka 6 i koraka 4 se uspostavlja petlja, dok se ne
              pronađe optimalan set parametara (hiperparametara u koraku 6), a
              ukoliko je potrebno proceniti vise modela kandidata, onda se
              uspostavlja petlja i između koraka 6 i 3.
            </p>
            <h3>
              3.1. Prikupljanje podataka
            </h3>
            <article>
              <p>
                Podaci koji se koriste u procesu razvoja neuronske mreže se
                dobijaju iz ponude oglasa tabela produkcione baze podataka. Kao
                prototip su uzete 2 zemlje, Nemačka i SAD, jer imaju najveću
                ponudu oglasa, samim tim je kao rezultat očekivana preciznost
                predikcije najveća. To su tabele car_germany i car_unitedstates.
              </p>
              <p>
                Baza podataka se osvežava 3 puta nedeljno i za svaku novu
                verziju baze se prave tabele razlike izmedju nove i stare
                verzije, posto su podaci iz stare verzije vec uzeti u obzir
                ranije, pa ih nema potrebe ponovo obrađivati. To su tabele
                diff_germany i diff_unitedstates. Kako vreme ide, tako niz tih
                tabela raste, ali je potrebno ustanoviti optimalnu vrednost za
                koju se povećavanjem broja verzija na ulazu povećava preciznost
                predikcije na izlazu, jer posle nekog trenutka najstarija
                verzija baze unosi veću grešku nego preciznost usled efekta
                zastarevanja podataka. Za početak je predviđeno da taj broj bude
                30, što odgovara svim oglasima koji su objavljeni u poslednja 2
                meseca, približno, što će se vremenom testirati i podešavati dok
                se ne dođe do optimalne vrednosti.
              </p>
              <p>
                Ovaj proces je potpuno automatizovan i sastoji se od 2 koraka.
                Prvi je kreiranje dumpova diff tabela u skriptu koji služi za
                generisanje nove baze na crawlDB instanci. Drugi je preuzimanje,
                skladištenje i insertovanje tih dumpova u bazu podataka od
                strane skripta koji se pokreće cronom na EC2 mašini, što je
                preduslov za nastavak obrade.
              </p>
            </article>
            <h3>
              3.2 Priprema podataka
            </h3>
            <article>
              <p>
                Kao sto je vec receno u poglavlju 2.1.2, postoje 2 tipa
                parametara: kategorijski i numerički. Kategorijski su model,
                trim, tip karoserije, tip goriva, tip menjača, boja i grad, a
                numerički godište, kilometraža, zapremina i snaga.
              </p>
              <p>
                Kao što je rečeno, neuronska mreža kao ulaz mora da dobije
                tenzor, što znači da je potrebno kategorijske parametre na neki
                način transformisati u numeričku reprezentaciju što se može
                postići postupkom “one-hot encoding”. Inicijalno je ovaj
                postupak bio uključen, jer je prvobitno izabran model XGBoost
                koji je to zahtevao. Kasnije je ipak izabrana naprednija verzija
                modela CatBoost koja je posebno prilagodjena za rad sa
                kategorijskim podacima i koja ne zahteva ovaj korak u pripremi.
              </p>
              <p>
                Drugi korak u pripremi je ipak neophodno ispoštovati, a odnosi
                se na utvrđivanje i supstituciju nepostojećih (nepoznatih)
                vrednosti za svaki parametar NaN vrednoscu, pošto vrednost
                takvih parametara nije uniformna za sve kolone. Na taj način su
                neuronskoj mreži jednoznačno i uniformno predstavljene sve
                šupljine u setu podataka, kako bi ih tretirala pravilno u daljim
                koracima.
              </p>
            </article>
            <h3>
              3.3 Izbor modela
            </h3>
            <article>
              <p>
                Ne postoji egzaktan potupak i metod procene koji unapred može da
                utvrdi koji model je optimalan za problem koji treba da se reši,
                tako da se početni izbor svodi na intuiciju i iskustvo, pa svaki
                sledeći se upoređuje sa prethodnim, kako bi se iterativno došlo
                do optimalnog modela sa optimalnim setom hiperparametara. Pri
                tome, modeli, algoritmi i heuristike se svakim danom razvijaju
                tako da je nezahvalno bilo koji izbor proglasiti konačnim.
              </p>
              <h4>
                3.3.1 Random forest
              </h4>
              <article>
                <p>
                  Za opisani problem, početni izbor je bio Random forest,
                  odnosno Random decision forest. Ovaj algoritam je nastao 1995.
                  kada je autor Tin Kam Ho koristeći metodu “attribute bagging”
                  implementirao stohastičku diskriminaciju za potrebe
                  klasifikovanja.
                </p>
                <p>
                  Attribute bagging je skracćenica od izraza bootstrap
                  aggregating iza kog stoji ideja da se od skupa podataka za
                  treniranje veličine n generise m manjih, različitih setova n’
                  i na taj način se povećava stabilnost i preciznost masinskog
                  učenja. Prednosti su da više slabijih stabala daju bolje
                  rezultate nego jedan jak koji je generisan nad celim setom
                  podataka i pritom se smanjuje varijansa, mada po cenu
                  povecanja bias-a i smanjenja interpretabilnosti modela.
                </p>
                <p>
                  Princip rada je da se neki entitet, za koji je potrebno doneti
                  odluku, procenjuje kroz svako stablo u šumi, kroz odgovarajuće
                  čvorove na putanji odlučivanja. Na taj način svako stablo daje
                  svoj glas u toj proceni i na kraju se ti glasovi sabiraju i
                  donosi se konačna odluka na nivou cele šume, kao što je
                  prikazano na slici.
                </p>
                <p class="text-center">
                  <img class="mw-100" src="@/images/ai/Random_forest_diagram_complete.png" />
                </p>
              </article>
              <h4>
                3.3.2 XGBoost
              </h4>
              <article>
                <p>
                  Random forest je pokazao glavne nedostatke zbog izvesnog
                  procenta šupljina u podacima, odnosno NaN vrednosti. To
                  izvesno unosi šum kad je parametar koji se ispituje NaN u
                  nekom čvoru, jer ne može da se donese osnovana odluka u koju
                  granu treba nastaviti tok odlučivanja posle takvog čvora. Iz
                  tog razloga prirodno je bilo potražiti sledećeg kandidata za
                  model i izbor je pao na XGBoost, odnosno Extreme Gradient
                  Boosting.
                </p>
                <p>
                  Kao i u random forest pristupu i XGBoost se zasniva na
                  kolekciji stabala odlučivanja, ali sa razlikom da nisu nastala
                  bagging postupkom, nego boosting.
                </p>
                <p class="text-center">
                  <img class="mw-100" src="@/images/ai/xgboost_vs_rf.png" />
                </p>
                <p>
                  Gradient boosting algoritam ima za cilj da smanji funkciju
                  greške koristeći princip da slaba stabla uče na rezidualnim
                  greškama jakih stabala, pri čemu funkcija greške može biti
                  proizvoljno zadata. Na taj način se ne menja distribucija
                  uzorka, a treningom nad rezidualnim greškama posebna pažnja se
                  posvećuje pogrešnim procenama, pa se na taj način iterativno
                  koriguje procena i smanjuje funkcija greške.
                </p>
              </article>
              <h4>
                3.3.3 CatBoost
              </h4>
              <article>
                <p>
                  Najveći problem u upotrebi XGBoost modela je to što je u
                  datasetu na ulazu većina kolona je kategorijske prirode, a ne
                  numeričke. To sa sobom povlači zahtevnu pripremu kao što je
                  opisano u 3.2, a što je još važnije, kao posledica toga,
                  memorija kao limitirani resurs se pokazala kao presudni
                  problem koji je upućivao na sledeće rešenje - CatBoost
                </p>
                <p>
                  CatBoost je razvijen od strane Yandex-a, takođe je zasnovan na
                  gradient boosting algoritmu, sa bitnom razlikom da podržava
                  kategorijske podatke. Zbog toga je nešto malo sporiji u režimu
                  treniranja od XGBoost, ali je zato memorijski daleko manje
                  zahtevan.
                </p>
              </article>
            </article>
            <h3>
              3.4 Treniranje
            </h3>
            <article>
              <p>
                Као što je objašnjeno u 3.1 diff tabele se kreiraju 3 puta
                nedeljno i svaki put kad se ti podaci preuzmu i pripreme, sledi
                niz koraka koji se sastoji od treniranja, generisanja
                parcijalnog modela i integracije novog parcijalnog modela u
                globalni (integralni) model. Treniranje i generisanje
                parcijalnog modela kao rezultat su postupci koji se automatski
                obavljaju na jednoznačan način. Integrisanje parcijalnog modela
                u globalni zahteva dodatne korake, koji se sastoje od toga da se
                važnost svakog novog stabla u parcijalnoj šumi ponderiše,
                odnosno skalira, jer nije svako stablo podjednako bitno za
                predikciju, različita stabla unose različitu grešku.
              </p>
            </article>
            <h3>
              3.5 Evaluacija – procena greške
            </h3>
            <article>
              <p>
                Procena greške je bitan korak u mašinskom učenju koji ima za
                cilj optimizaciju parametara modela da bi ona bila najmanja
                moguća. U našem slučaju postoje 3 različita postupka za procenu
                greške: za parcijalni model, za integralni model i za hiper
                parametre (koji će biti opisan u poglavlju 3.6.).
              </p>
              <p>
                Postupak za procenu greške parcijalnog modela služi da se sa
                fiksnim hiper parametrima u jednoj iteraciji pokaže koliko taj
                parcijalni model greši nad tim setom podataka i on je sastavni
                deo samog programa mašinskog učenja.
              </p>
              <p class="text-center">
                <img class="mw-100" src="@/images/ai/ml_error_est.png" />
              </p>
              <p>
                Procena greške integralnog modela je provera da li i do koje
                mere integralni model konvergira i daje odgovor na pitanje koji
                je to broj parcijalnih modela koji treba da se integriše sa
                ciljem povećanja tačnosti. Taj korak nije deo programa mašinskog
                učenja, nego se izvodi zasebnim alatima i mehanizmima nad svakom
                novom bazom, a ne samo diferencijalnom kao što je slučaj sa
                evaluacijom parcijalnog modela.
              </p>
            </article>
            <h3>3.6 Podešavanje hiperparametara</h3>
            <article>
              <p>
                Svaka heuristika matematičkog učenja pre nego što se uopšte
                krene sa učenjem mora podesiti za dati skup podataka. To se radi
                tako što se odaberu neki podrazumevani hiperparametri (parametri
                same heuristike koji moraju da se zadaju da bi uopšte ona
                radila) koji sigurno daju kakvo takvo smisleno predviđanje posle
                treniranja mreže, a zatim se oni podešavaju tako što se
                heuristika iznova i iznova trenira nad datim skupom podataka.
                Algoritmi koji se koriste za optimizaciju samih hiperparametara
                se upotrebljavaju u ovoj fazi mašinskog učenja. Samih algoritama
                ima puno ali mi smo se opredelili za tri jer su istraživanja
                pokazala da oni daju dobre rezultate za heuristike mašinskog
                učenja koje mi koristimo.
              </p>
              <h4>
                3.6.1 Random Search -- Slučajno pretraživanje (RS)
              </h4>
              <article>
                <p>
                  Slučajno pretraživanje (RS) je familija metoda numeričke
                  optimizacije koje ne zahtevaju optimizaciju gradijenta
                  problema, pa se RS može koristiti na funkcijama koje nisu
                  kontinuirane ili diferencibilne. Takve metode optimizacije su
                  takođe poznate kao metode direktne pretrage, metode bez
                  derivata ili metode crne kutije.
                </p>
                <p>
                  Izumitelj metode Anderson je 1953. pregledao napredak metoda u
                  pronalaženju maksimuma ili minimuma problema koristeći seriju
                  nagađanja distribuiranih određenim redosledom ili šablonom u
                  prostoru za pretragu parametara, npr. konfuzan dizajn sa
                  eksponencijalno raspoređenim razmacima/koracima. Ova pretraga
                  se nastavlja uzastopno na svakom parametru i iterativno se
                  usavršava na najboljim pretpostavkama iz poslednje sekvence.
                  Obrazac može biti mreža (faktorska) pretraga svih parametara,
                  sekvencijalna pretraga svakog parametra ili kombinacija oba.
                </p>
              </article>
              <h4>
                3.6.2 Tree of Parzen Estimators (TPE)
              </h4>
              <article>
                <p>
                  SMBO algoritmi su korišćeni u mnogim aplikacijama gde je
                  procena optimizacione funkcije skupa. U aplikaciji u kojoj je
                  stvarna optimizaciona funkcija f : Ks → R skupa za procenu,
                  algoritmi zasnovani na modelu aproksimiraju f sa surogatom
                  koji je jeftiniji za procenu. Obično je unutrašnja petlja u
                  SMBO algoritmu numerička optimizacija ovog surogata, ili neka
                  transformacija surogata. Tačka k∗ koja maksimizira surogat
                  (ili njegovu transformaciju) postaje predlog gde treba
                  proceniti pravu funkciju f. SMBO algoritmi se razlikuju po
                  tome koji kriterijum optimizuju da bi dobili k∗ dat model (ili
                  surogat) od f, i po modelu f preko istorije posmatranja H.
                </p>
                <p>
                  Algoritmi u ovom radu optimizuju kriterijum očekivanog
                  poboljšanja (EI). Predloženi su i drugi kriterijumi, kao što
                  su verovatnoća poboljšanja i očekivano poboljšanje,
                  minimiziranje uslovne entropije minimizatora i kriterijum
                  zasnovan na banditima opisan u. Odlučili smo da koristimo EI
                  kriterijum u našem radu jer je intuitivan i pokazalo se da
                  dobro funkcioniše u različitim okruženjima. Sistematsko
                  istraživanje kriterijuma poboljšanja ostavljamo za budući rad.
                  Očekivano poboljšanje je očekivanje prema nekom modelu M od f
                  : Ks → RN da će f(k) premašiti (negativno) neki prag i∗.
                </p>
                <p>
                  Predviđajući da će naši zadaci optimizacije hiperparametara
                  značiti velike dimenzije i male budžete za procenu
                  optimizacione funkcije, sada se okrećemo drugoj strategiji
                  modeliranja i šemi optimizacije EI za SMBO algoritam.
                </p>
                <p>
                  Konfiguracioni prostor Ks je opisan generativnim procesom
                  strukturiranim grafom (npr. prvo izaberite broj slojeva, a
                  zatim izaberite parametre za svaki). Parzenov estimator sa
                  strukturom drveta (TPE) modelira p(k|i) transformacijom tog
                  generativnog procesa, zamenjujući distribucije prethodne
                  konfiguracije neparametarskim gustinama. U eksperimentalnom
                  delu videćemo da je konfiguracioni prostor opisan korišćenjem
                  uniformnih, log-uniformnih, kvantizovanih log-uniformnih i
                  kategoričkih varijabli. U ovim slučajevima, TPE algoritam
                  pravi sledeće zamene: uniformna → skraćena Gausova smeša,
                  log-uniform → eksponencijalna skraćena Gausova smeša,
                  kategorična → re-ponderisana kategorička. Koristeći različita
                  zapažanja {k(1), ..., k(k)} u neparametarskim gustinama, ove
                  zamene predstavljaju algoritam učenja koji može proizvesti
                  različite gustine u konfiguracionom prostoru Ks.
                </p>
                <p>
                  TPE algoritam bira i∗ da bude neki kvantil g posmatranih
                  vrednosti i, tako da je p(i &lt; i∗) = g, ali nije neophodan
                  poseban model za p(i). Održavanjem sortiranih lista
                  posmatranih promenljivih u H, vreme izvođenja svake iteracije
                  TPE algoritma može se linearno skalirati u |H| i linearno u
                  broju promenljivih (dimenzija) koje se optimizuju.
                </p>
              </article>
              <h4>
                3.6.3 Adaptive TPE (A-TPE)
              </h4>
              <article>
                <p>
                  Na osnovu istraživanja nekoliko načina za poboljšanje
                  rezultata TPE algoritma u određenim situacijama, razvijen je
                  novi algoritam, Adaptive Tree of Parzen Estimators, ili
                  skraćeno ATPE. ATPE ima više hiperparametara od TPE (u obliku
                  jednačina koje se koriste za postavljanje sopstvenih
                  hiperparametara TPE), ali ti hiperparametri se mogu izvesti iz
                  podataka, kako iz naših simuliranih hiperparametarskih
                  prostora, tako i (na kraju) podataka iz stvarnog sveta iz
                  mnogih stvarnih pretraga hiperparametara.
                </p>
                <p>
                  ATPE je izgrađen na četiri osnovna poboljšanja u odnosu na
                  TPE:
                </p>
                <ul>
                  <li>
                    Automatsko podešavanje gama na osnovu kardinalnosti vašeg
                    hiperparametarskog prostora i nagiba i standardne devijacije
                    rezultata
                  </li>
                  <li>
                    Automatsko podešavanje N EI estimatora na osnovu
                    kardinalnosti prostora hiperparametara
                  </li>
                  <li>
                    Smanjenje broja rundi inicijalizacije sa 20 na 10
                  </li>
                  <li>
                    Optimizacija samo podskupa hiperparametara u svakom
                    ispitivanju. Podskup se bira na osnovu koeficijenata
                    korelacije preko mehanizma opisanog u odeljku „Ponderisanje
                    parametara“
                  </li>
                </ul>
              </article>
            </article>
            <h3>
              3.7 Predikcija
            </h3>
            <article>
              <p>
                Kad procena greške integralnog modela pokaže zadovoljavajuće
                rezultate, model je spreman za produkcionu primenu. Za sada
                postoje 2 nezavisna oblika upotrebe predikcije: prilikom
                generisanja nove produkcione baze i kao servis za procenu
                vrednosti vozila sa proizvoljnim parametrima.
              </p>
              <h4>
                3.7.1 Predikcija pri generisanju produkcione baze
              </h4>
              <article>
                <p>
                  Postupak generisanja i prebacivanja produkcione baze se
                  sastoji iz niza koraka, koji se ponavljaju za svaku zemlju.
                  Pošto su za sad AI sistemom podržane USA i Nemačka, za njih je
                  postupak nešto izmenjen u odnosu na ostale zemlje. U trenutku
                  kad su car_germany, odnosno car_unitedstates tabele populisane
                  podacima, program za generisanje baze šalje zahtev AI serveru
                  koji treba da prođe kroz sve rekorde i odradi evaluaciju,
                  odnosno update kolone estPriceEUR (procenjena vrednost) i deal
                  (klasifikovana razlika između procenjene i oglašene
                  vrednosti). Pošto bi update svakog pojedinačnog rekorda bio
                  suviše spor, pribegava se optimizaciji na takav način što se
                  napravi privremena tabela tmp_ai, gde se navedene kolone
                  populišu za sve rekorde, a onda na kraju odradi bulk update
                  svih rekorda jednim upitom, spajanjem car i tmp_ai tabela.
                </p>
              </article>
              <h4>
                3.7.2 Predikcija kao servis za procenu vrednosti
              </h4>
              <article>
                <p>
                  Sledeća primena AI sistema je da se za proizvoljan automobil,
                  kome su poznati svi potrebni parametri, oni proslede
                  prediktoru koji procenjuje vrednost takvog automobila. Dakle,
                  automobil sa tim parametrima uopšte ne mora da postoji u nasoj
                  bazi, možda jos uvek nigde nije ni oglašen (ako prodavac zeli
                  da stekne predstavu koliko njegov automobil vredi na tržištu
                  pre nego što bi ga oglasio) ili je potencijalni kupac video
                  automobil koji je na prodaju, ali van uobičajenih sredstava
                  oglasavanja.
                </p>
                <p>
                  Druga varijacija ove primene je ako je potrebno ispitati koja
                  je zavisnost cene automobila od nekog posebnog parametra. U
                  tom slučaju su svi parametri osim jednog poznati, a taj
                  nepoznati se kreće u domenu mogućih vrednosti, pa se može
                  dobiti vizuelna reprezentacija te zavisnosti u obliku grafika.
                </p>
              </article>
            </article>
          </article>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
export default {};
</script>

<style></style>
